import type {
	CloudRecording,
	CloudRecordingVideo,
	DownloadableMedia,
	DownloadMediaResult,
} from '../types/cloud-recordings';
import { RecordingMediaType } from '../types/cloud-recordings';
import { DEFAULT_OBJECT_ICON, getObjectLabels } from './camera-objects';
import type { LabelProps } from '@Shelly/shelly-design-system/index';
import { format } from 'svelte-i18n';
import { get } from 'svelte/store';
import { app, os } from 'src/stores/session/native-bridge';
import { dateFormat } from 'src/helpers/date_helper';
import { real_id } from 'src/helpers/interface_helpers';
import { downloadRecordingFile } from 'src/services/cam-cloud/recordings';
import { fetchSignedMedia, getSignedMediaUrl } from 'src/services/cam-cloud/media-auth';
import { awaitDeviceKey } from './recording-key-management';
import { deriveImageKey, deriveRecordingKey } from './encryption/recording-key';
import { decryptThumbnail } from './encryption/recording-image-decryption';
import { bytesToBase64 } from './encryption/encoding';

const _format = get(format);

/**
 * Determines whether a recording is an HLS stream, which must be played through
 * hls.js rather than a plain `<video>` element.
 *
 * @param recording - The cloud recording to inspect.
 * @returns `true` when the recording is a video whose URL is an `.m3u8` playlist.
 */
export const isHlsRecording = (recording: CloudRecording | null | undefined): boolean => {
	if (!recording || recording.type !== RecordingMediaType.VIDEO) return false;
	const videoUrl = (recording as CloudRecordingVideo)?.videoUrl;
	return typeof videoUrl === 'string' && videoUrl.split('?')[0].toLowerCase().endsWith('.m3u8');
};

export const getRecordingEventLabel = (recording: CloudRecording) => {
	let labelList: LabelProps[] = [];
	switch (recording.event) {
		case 'motion':
			labelList.push({
				value: _format('motion', { default: 'Motion' }),
				icon: 'fas fa-running',
			});
			break;
		case 'image':
			labelList.push({
				value: _format('camera_recording_image', { default: 'Image' }),
				icon: 'fas fa-image',
			});
			break;
		case 'manual':
			if (recording.type === RecordingMediaType.VIDEO) {
				labelList.push({
					value: _format('camera_recording_event_manual_video', { default: 'Video' }),
					icon: 'fas fa-video',
				});
			} else {
				labelList.push({
					value: _format('camera_recording_event_manual_image', { default: 'Snapshot' }),
					icon: 'fas fa-camera',
				});
			}
			break;
		default:
			labelList.push({
				value: recording.event,
				icon: DEFAULT_OBJECT_ICON,
			});
			break;
	}

	labelList.push(...getObjectLabels(recording.classification?.objects ?? []));

	return labelList;
};

/** Resolve the best media source (and its type) for a recording, if any. */
const getDownloadableMedia = (recording: CloudRecording): DownloadableMedia | null => {
	const videoUrl = (recording as CloudRecordingVideo)?.videoUrl;
	if (recording?.type === RecordingMediaType.VIDEO && videoUrl) {
		return { url: videoUrl, extension: 'mp4' };
	}
	if (recording?.type === RecordingMediaType.IMAGE && recording.imageUrls?.large) {
		return { url: recording.imageUrls.large, extension: 'jpg', imageSize: 'large' };
	}
	return null;
};

/**
 * A recording can be downloaded when there is a single file to save: an image
 * (decrypted here in the browser when encrypted), an unencrypted video that is
 * not an HLS playlist, or an encrypted video, which the cloud reassembles into
 * an MP4 for us.
 */
export const isDownloadableMedia = (recording: CloudRecording): boolean => {
	if (recording?.type === RecordingMediaType.VIDEO) {
		if (!(recording as CloudRecordingVideo)?.videoUrl) return false;
		return !!recording.encrypted || !isHlsRecording(recording);
	}
	return getDownloadableMedia(recording) !== null;
};

const buildRecordingFilename = (recording: CloudRecording, extension: string): string => {
	const datePart = dateFormat(recording.date, 'Y-m-d_H-i-s');
	const eventPart = (recording.event || 'recording').replace(/[^a-z0-9]+/gi, '-');
	return `ShellyCamera-${eventPart}-${datePart}.${extension}`;
};

const DOWNLOAD_OK: DownloadMediaResult = { ok: true };
const DOWNLOAD_LOCKED: DownloadMediaResult = { ok: false, reason: 'locked' };
const DOWNLOAD_ERROR: DownloadMediaResult = { ok: false, reason: 'error' };

const blobToBase64 = (blob: Blob): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve((reader.result as string).split(',')[1] ?? '');
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(blob);
	});

/** Save an in-memory file, via the data URL the Android wrapper expects or an object URL. */
const saveBlob = async (blob: Blob, filename: string): Promise<boolean> => {
	const a = document.createElement('a');
	a.setAttribute('download', filename);

	if (get(app) && get(os) !== 'IOS') {
		const base64 = await blobToBase64(blob).catch(() => '');
		if (!base64) return false;
		a.setAttribute('href', `data:application/octet-stream;filename=${filename};base64,${base64}`);
		a.click();
		return true;
	}

	a.setAttribute('target', '_blank');
	const windowURL = window.webkitURL || window.URL;
	const url = windowURL.createObjectURL(blob);
	a.setAttribute('href', url);
	a.click();
	windowURL.revokeObjectURL(url);
	return true;
};

/** Download media that is served as-is, straight from its signed cloud URL. */
const downloadPlainMedia = async (recording: CloudRecording): Promise<DownloadMediaResult> => {
	const media = getDownloadableMedia(recording);
	if (!media) return DOWNLOAD_ERROR;

	const filename = buildRecordingFilename(recording, media.extension);

	// App: hand the signed URL to the wrapper to save the file.
	if (get(app)) {
		const signedUrl = await getSignedMediaUrl(media.url);
		if (!signedUrl) return DOWNLOAD_ERROR;

		const a = document.createElement('a');
		a.setAttribute('target', '_blank');
		a.setAttribute('download', filename);
		a.setAttribute('href', signedUrl);
		a.click();
		return DOWNLOAD_OK;
	}

	// Browser: fetch as a blob so the click downloads instead of opening a tab.
	const response = await fetchSignedMedia(media.url);
	if (!response?.ok) return DOWNLOAD_ERROR;

	const blob = await response.blob();
	return (await saveBlob(blob, filename)) ? DOWNLOAD_OK : DOWNLOAD_ERROR;
};

const errorStatus = (error: unknown): number | undefined => (error as { status?: number })?.status;

const errorText = (error: unknown): string => {
	const data = (error as { response?: { data?: { error?: string; message?: string } } })?.response
		?.data;
	return `${data?.error ?? ''} ${data?.message ?? ''}`.toLowerCase();
};

/**
 * Download an encrypted recording: the cloud reassembles and decrypts it once we
 * hand it the same `rec_key` playback derives.
 *
 * @param retryOnWrongKey - Whether a rejected key may reopen the password prompt;
 * cleared for the retry so a second rejection cannot loop.
 */
const downloadEncryptedVideo = async (
	recording: CloudRecording,
	masterKey: string,
	retryOnWrongKey = true,
): Promise<DownloadMediaResult> => {
	const recKey = await deriveRecordingKey(masterKey, real_id(recording.mac), recording.id);
	if (!recKey) return DOWNLOAD_ERROR;

	let blob: Blob | null = null;
	try {
		blob = await downloadRecordingFile(recording.id, bytesToBase64(recKey));
	} catch (error) {
		if (errorStatus(error) !== 400) return DOWNLOAD_ERROR;
		// The endpoint serves encrypted recordings only; the rest are plain files.
		if (errorText(error).includes('not encrypted')) return downloadPlainMedia(recording);
		if (!retryOnWrongKey) return DOWNLOAD_ERROR;

		const recoveredKey = await awaitDeviceKey(recording.mac, recording.date, true);
		if (!recoveredKey) return DOWNLOAD_LOCKED;
		return downloadEncryptedVideo(recording, recoveredKey, false);
	}

	if (!blob) return DOWNLOAD_ERROR;
	const filename = buildRecordingFilename(recording, 'mp4');
	return (await saveBlob(blob, filename)) ? DOWNLOAD_OK : DOWNLOAD_ERROR;
};

/**
 * Download an encrypted image: unlike video, there is no server-side decryption
 * endpoint for images, so the file is fetched as-is and decrypted here with the
 * same AES-256-GCM key and size-derived nonce the thumbnails are displayed with.
 *
 * @param retryOnWrongKey - Whether a rejected key may reopen the password prompt;
 * cleared for the retry so a second rejection cannot loop.
 */
const downloadEncryptedImage = async (
	recording: CloudRecording,
	masterKey: string,
	retryOnWrongKey = true,
): Promise<DownloadMediaResult> => {
	const media = getDownloadableMedia(recording);
	if (!media?.imageSize) return DOWNLOAD_ERROR;

	const imageKey = await deriveImageKey(masterKey, real_id(recording.mac), recording.id);
	if (!imageKey) return DOWNLOAD_ERROR;

	const response = await fetchSignedMedia(media.url).catch(() => null);
	if (!response?.ok) return DOWNLOAD_ERROR;
	const encrypted = await response.arrayBuffer();

	let decrypted: ArrayBuffer;
	try {
		decrypted = await decryptThumbnail(encrypted, imageKey, media.imageSize);
	} catch {
		// GCM only rejects on a failed tag check, so this is a wrong/rotated key.
		if (!retryOnWrongKey) return DOWNLOAD_ERROR;
		const recoveredKey = await awaitDeviceKey(recording.mac, recording.date, true);
		if (!recoveredKey) return DOWNLOAD_LOCKED;
		return downloadEncryptedImage(recording, recoveredKey, false);
	}

	const blob = new Blob([decrypted], { type: 'image/jpeg' });
	const filename = buildRecordingFilename(recording, media.extension);
	return (await saveBlob(blob, filename)) ? DOWNLOAD_OK : DOWNLOAD_ERROR;
};

/** Download a cloud recording (video or image) to the device. */
export const downloadMedia = async (recording: CloudRecording): Promise<DownloadMediaResult> => {
	if (!isDownloadableMedia(recording)) return DOWNLOAD_ERROR;

	if (recording.encrypted) {
		const masterKey = await awaitDeviceKey(recording.mac, recording.date);
		if (!masterKey) return DOWNLOAD_LOCKED;
		if (recording.type === RecordingMediaType.VIDEO) {
			return downloadEncryptedVideo(recording, masterKey);
		}
		return downloadEncryptedImage(recording, masterKey);
	}

	return downloadPlainMedia(recording);
};
